const WEBVIEW_SESSION_KEY = 'freeflow-webview';

function isWebViewQueryParam(value: string | null): boolean {
  return value === 'true' || value === '1';
}

/**
 * Acesso ao sessionStorage protegido por try/catch.
 * No iOS WKWebView (e Safari em modo privado) o storage pode lançar exceção;
 * sem essa proteção a detecção quebraria a renderização no iOS.
 */
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage indisponível (iOS WKWebView/privado) — ignora */
  }
}

/** Detecta ponte nativa de WebView em RN, iOS (WKWebView) e Android. */
function hasNativeWebViewBridge(): boolean {
  const win = window as any;

  if (win.ReactNativeWebView) {
    return true;
  }

  // iOS WKWebView nativo expõe handlers via window.webkit.messageHandlers
  const messageHandlers = win.webkit?.messageHandlers;
  if (messageHandlers?.close || messageHandlers?.callbackHandler) {
    return true;
  }

  // Android nativo expõe a interface window.Android
  if (typeof win.Android?.finish === 'function') {
    return true;
  }

  return false;
}

/** Persiste webview (URL ou ponte nativa) para sobreviver à limpeza do WebAccessToken. */
export function captureWebViewFromUrl(): void {
  const webviewParam = new URLSearchParams(window.location.search).get('webview');
  if (isWebViewQueryParam(webviewParam) || hasNativeWebViewBridge()) {
    safeSessionSet(WEBVIEW_SESSION_KEY, 'true');
  }
}

/** Remove apenas o token da URL, preservando webview e demais parâmetros. */
export function stripWebAccessTokenFromUrl(): void {
  const cleanUrl = new URL(window.location.href);
  const hasToken =
    cleanUrl.searchParams.has('WebAccessToken') ||
    cleanUrl.searchParams.has('webAccessToken');

  if (!hasToken) {
    return;
  }

  cleanUrl.searchParams.delete('WebAccessToken');
  cleanUrl.searchParams.delete('webAccessToken');
  window.history.replaceState(
    {},
    document.title,
    cleanUrl.pathname + cleanUrl.search + cleanUrl.hash,
  );
}

function checkIsWebView(): boolean {
  captureWebViewFromUrl();

  const webviewParam = new URLSearchParams(window.location.search).get('webview');
  if (isWebViewQueryParam(webviewParam)) {
    return true;
  }

  if (safeSessionGet(WEBVIEW_SESSION_KEY) === 'true') {
    return true;
  }

  return hasNativeWebViewBridge();
}

export function useIsWebView(): boolean {
  return checkIsWebView();
}

/**
 * Envia mensagem para fechar a WebView.
 * Tenta ReactNativeWebView.postMessage, iOS WKWebView, Android e window.postMessage como fallback.
 */
export function closeWebView(): void {
  const message = JSON.stringify({ type: 'close' });

  try {
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(message);
      return;
    }

    if ((window as any).webkit?.messageHandlers?.close) {
      (window as any).webkit.messageHandlers.close.postMessage(message);
      return;
    }

    if ((window as any).webkit?.messageHandlers?.callbackHandler) {
      (window as any).webkit.messageHandlers.callbackHandler.postMessage(message);
      return;
    }

    if ((window as any).Android?.finish && typeof (window as any).Android.finish === 'function') {
      (window as any).Android.finish();
      return;
    }

    window.postMessage(message, window.location.origin);
  } catch (error) {
    console.warn('Erro ao tentar fechar webview:', error);
  }
}

export default useIsWebView;
